import type { ReactNode } from "react";
import { BadgeSizeContext } from "../Display/BadgeSizeContext";

interface KeyValuePair {
  key: string;
  value: ReactNode;
}

interface KeyValueCellProps {
  items: KeyValuePair[];
  /** Number of columns for grid layout (default: 1) */
  columns?: 1 | 2 | 3 | undefined;
}

function hasContent(value: ReactNode): boolean {
  if (value === undefined || value === "" || value === "—") return false;
  if (typeof value === "string") return value.trim().length > 0;
  return true;
}

const gridClasses = {
  1: "grid-cols-1",
  2: "grid-cols-2",
  3: "grid-cols-3",
} as const;

export function KeyValueCell({ items, columns = 1 }: KeyValueCellProps) {
  const populated = items.filter((item) => hasContent(item.value));

  if (populated.length === 0) {
    return (
      <span className="text-xs font-medium text-content-faint">{"\u2014"}</span>
    );
  }

  return (
    <BadgeSizeContext.Provider value="small">
      <div className={`grid gap-x-4 gap-y-1.5 py-0.5 ${gridClasses[columns]}`}>
        {populated.map((item) => (
          <div key={item.key} className="flex flex-col gap-0.5">
            {/* An eyebrow above the value, not a label beside it: in a table the
                value is what gets scanned, so it leads. Uppercase and muted are
                what mark this as a label — before, key and value resolved to the
                same hex at the same weight, so neither led. */}
            <div className="text-[10px] leading-none font-semibold tracking-wide text-content-muted uppercase">
              {item.key}
            </div>
            <div className="text-[13px] leading-snug font-semibold text-foreground">
              {item.value}
            </div>
          </div>
        ))}
      </div>
    </BadgeSizeContext.Provider>
  );
}
